import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Clock, Download, RotateCw, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { inr, longDate, startPayment, errMsg, attemptLabel, attemptTone } from "./runShared";
import { RUN_STYLES } from "./RunFormParts";

const Info = ({ label, value, testid }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8478] font-semibold">{label}</div>
    <div className="text-sm font-medium text-[#1A1A1A] break-words" data-testid={testid}>{value || "—"}</div>
  </div>
);

function StatusHead({ reg }) {
  const paid = reg.payment_status === "PAID";
  const failed = ["FAILED", "CANCELLED"].includes(reg.payment_status) || reg.last_attempt_status === "VERIFICATION_FAILED";
  const Icon = paid ? CheckCircle2 : failed ? XCircle : Clock;
  const tone = paid ? "bg-[#E7F3F1] text-[#0D5C63]" : failed ? "bg-[#FDECEC] text-[#B02020]" : "bg-[#FEF7E4] text-[#8a5a1a]";
  const title = paid ? "You're registered!" : reg.payment_status === "CANCELLED" ? "Payment cancelled" : failed ? "Payment not completed" : "Payment pending";
  const sub = paid
    ? `Payment verified. Receipt sent to ${reg.email}.`
    : reg.last_attempt_message || "Your registration is saved but the fee has not been received yet. Complete the payment to confirm your entry.";
  return (
    <div className="text-center">
      <div className={`mx-auto h-16 w-16 rounded-full inline-flex items-center justify-center ${tone}`}><Icon size={34} /></div>
      <h1 className="run-display uppercase text-4xl sm:text-5xl text-[#08363A] mt-4" data-testid="run-status-title">{title}</h1>
      <p className="text-sm text-[#5B5B5B] mt-2 max-w-lg mx-auto" data-testid="run-status-sub">{sub}</p>
    </div>
  );
}

export default function RunStatus() {
  const { token } = useParams();
  const [reg, setReg] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/marathon/registrations/${token}`).then(({ data }) => setReg(data)).catch((e) => setErr(errMsg(e, "Registration not found")));
  }, [token]);

  const retry = async () => {
    setBusy(true);
    try { await startPayment(token); } catch (e) { toast.error(errMsg(e, "Could not start payment")); setBusy(false); }
  };
  const download = async () => {
    try {
      const res = await api.get(`/public/marathon/registrations/${token}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `Receipt-${reg.registration_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download receipt"); }
  };

  const shell = (child) => (
    <div className="run-page min-h-screen bg-[#FAF6EB]" data-testid="run-status-page">
      <style>{RUN_STYLES}</style>
      <div className="max-w-3xl mx-auto px-5 py-14 sm:py-20">{child}</div>
    </div>
  );
  if (err) return shell(<div className="bg-[#FDECEC] text-[#B02020] rounded-2xl p-6" data-testid="run-status-error"><AlertTriangle size={18} className="inline mr-2" />{err}</div>);
  if (!reg) return shell(<div className="text-sm text-[#5B5B5B]" data-testid="run-status-loading"><Loader2 size={16} className="inline animate-spin mr-2" />Verifying…</div>);

  const paid = reg.payment_status === "PAID";
  return shell(
    <div className="run-rise bg-white border border-[#E9E2D2] rounded-3xl p-6 sm:p-10">
      <StatusHead reg={reg} />
      {paid && (
        <div className="mt-8 mx-auto max-w-xs rounded-2xl bg-[#08363A] text-white text-center py-5 run-stripe" data-testid="run-bib">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">Bib Number</div>
          <div className="run-display text-6xl text-[#F26419] leading-none mt-1" data-testid="run-bib-number">{reg.bib_number}</div>
        </div>
      )}
      <div className="mt-8 grid sm:grid-cols-2 gap-4 bg-[#FAFAF7] border border-[#E9E2D2] rounded-2xl p-5">
        <Info label="Registration No." value={reg.registration_no} testid="run-status-regno" />
        <Info label="Participant" value={reg.full_name} />
        <Info label="Run" value={`${reg.run_name} · ${reg.distance_km} KM`} />
        <Info label="Event Date" value={longDate(reg.event_date)} />
        <Info label="Reporting / Venue" value={[reg.reporting_time, reg.venue].filter(Boolean).join(" · ")} />
        <Info label="Fee" value={inr(reg.fee)} testid="run-status-fee" />
        {paid && <Info label="Order ID" value={reg.payment?.order_id} testid="run-status-order" />}
        {paid && <Info label="Tracking ID" value={reg.payment?.tracking_id} />}
      </div>
      {reg.attempts?.length > 0 && (
        <div className="mt-6" data-testid="run-status-attempts">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8478] font-semibold mb-2">Payment attempts</div>
          <ul className="divide-y divide-[#E9E2D2] border border-[#E9E2D2] rounded-2xl overflow-hidden">
            {reg.attempts.map((a, i) => (
              <li key={a.order_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-white" data-testid={`run-status-attempt-${i}`}>
                <div>
                  <div className="text-sm">Order <span className="font-mono font-semibold" data-testid={`run-status-attempt-order-${i}`}>{a.order_id}</span></div>
                  <div className="text-[11px] text-[#8a8478]">{new Date(a.created_at).toLocaleString("en-IN")}{a.failure_reason && a.attempt_status !== "SUCCESS" ? ` · ${a.failure_reason}` : ""}</div>
                </div>
                <span className={`text-[10px] uppercase font-bold tracking-wider px-2.5 py-1 rounded-full ${attemptTone(a.attempt_status)}`} data-testid={`run-status-attempt-status-${i}`}>{attemptLabel(a.attempt_status)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        {paid ? (
          <button type="button" onClick={download} className="inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#F26419] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-full transition-colors" data-testid="run-download-receipt"><Download size={15} />Download Receipt</button>
        ) : (
          <button type="button" onClick={retry} disabled={busy} className="inline-flex items-center gap-2 bg-[#F26419] hover:bg-[#08363A] disabled:opacity-60 text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-full transition-colors" data-testid="run-retry-payment">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <RotateCw size={15} />}Pay {inr(reg.fee)} now
          </button>
        )}
        <Link to="/run/register" className="text-sm font-semibold text-[#0D5C63] hover:underline" data-testid="run-new-registration">New registration</Link>
      </div>
      <p className="mt-6 text-[11px] text-center text-[#8a8478]">Bookmark this page to check your status later. Questions? info@gsmandal.org</p>
    </div>
  );
}
